import React, { useCallback, useEffect, useState } from 'react';
import FloorPlanDisplayPage from './FloorPlanDisplayPage';

// ─── The tablet's own front door ──────────────────────────────────────────────
//
// /display is opened on the tablet at the office entrance. It is not a staff
// page: it never asks for the Entra ID session, and everything it fetches goes
// to /api/display/*, which knows only about display devices and today's board.
// Sign in with the username and password created in Desk & Vehicle
// Management. The session lasts 30 days, or ends after four days in which the
// display asked for nothing (src/api/display.js); while the screen is on it
// refreshes every minute, so only a display left switched off idles out.
//
// The screen itself is FloorPlanDisplayPage, unchanged. Only where its data comes
// from is different, which is the loadBoard prop.

async function displayFetch(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api/display/${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(data?.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.notice = data?.message || '';
    throw err;
  }
  return data;
}

function NavyShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ background: '#0D1D32', fontFamily: 'Montserrat, sans-serif' }}>
      <img src="/av-logo-white.png" alt="Alton Valley" style={{ height: 44, width: 'auto', objectFit: 'contain' }} className="mb-6" />
      {children}
    </div>
  );
}

function SignInScreen({ onSignedIn, notice = '' }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const res = await displayFetch('login', { method: 'POST', body: form });
      onSignedIn(res.device);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <NavyShell>
      <form onSubmit={submit} className="bg-white rounded-xl p-7 w-full max-w-[360px] shadow-2xl">
        <h2 className="text-[15px] font-bold text-gray-900 mb-1">Entrance display</h2>
        <p className="text-[11px] text-gray-500 mb-5">Sign this tablet in with its display username and password.</p>
        {/* Why it is signed out, when the session has just timed out. */}
        {notice && !error && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">{notice}</p>
        )}
        {error && <p className="text-[11px] text-red-600 font-semibold mb-3">{error}</p>}
        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Username</label>
        <input type="text" autoComplete="username" autoCapitalize="none" required value={form.username}
          onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))}
          className="w-full mb-3.5 rounded-md border border-gray-300 px-3 py-2.5 text-[14px] text-gray-900" />
        <label className="block text-[11px] font-semibold text-gray-700 mb-1">Password</label>
        <input type="password" autoComplete="current-password" required value={form.password}
          onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
          className="w-full mb-5 rounded-md border border-gray-300 px-3 py-2.5 text-[14px] text-gray-900" />
        <button type="submit" disabled={busy}
          className="w-full rounded-md py-3 text-[13px] font-bold text-white disabled:opacity-60"
          style={{ background: '#A1723D' }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="text-center text-[9px] text-gray-400 mt-4">
          Displays are set up under Desk &amp; Vehicle Management.
        </p>
      </form>
    </NavyShell>
  );
}

export default function FloorPlanDisplayShell() {
  // 'checking' | 'signed-out' | 'in'
  const [auth, setAuth] = useState('checking');
  const [device, setDevice] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    displayFetch('me')
      .then(me => {
        if (me?.authenticated) { setDevice(me.device); setAuth('in'); }
        else { setNotice(me?.message || ''); setAuth('signed-out'); }
      })
      .catch(() => setAuth('signed-out'));
  }, []);

  const loadBoard = useCallback(async (today) => {
    try {
      return await displayFetch(`board?date=${encodeURIComponent(today)}`);
    } catch (e) {
      // The session was ended from the office (password reset, device switched
      // off) or timed out. Go back to the sign-in rather than showing a stale
      // board indefinitely.
      if (e.status === 401) { setDevice(null); setNotice(e.notice || ''); setAuth('signed-out'); }
      throw e;
    }
  }, []);

  const signOut = async () => {
    await displayFetch('logout', { method: 'POST', body: {} }).catch(() => {});
    setDevice(null);
    setAuth('signed-out');
  };

  if (auth === 'checking') {
    return <NavyShell><div className="text-white/50 text-[11px]">Starting the display…</div></NavyShell>;
  }
  if (auth === 'signed-out') {
    return <SignInScreen notice={notice} onSignedIn={(d) => { setDevice(d); setNotice(''); setAuth('in'); }} />;
  }
  return <FloorPlanDisplayPage locationId={device?.location_id || null} loadBoard={loadBoard} onSignOut={signOut} />;
}
